type DocumentLike = Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>;

export class VisibilityManager {
  private doc: DocumentLike;
  private now: () => number;
  private backgroundEntryTime: number | null = null;
  private backgroundCallbacks = new Set<() => void>();
  private foregroundCallbacks = new Set<(durationMs: number) => void>();
  private handler: (ev: Event) => void;

  constructor(doc?: DocumentLike, now?: () => number) {
    this.doc = doc ?? document;
    this.now = now ?? (() => Date.now());
    this.handler = () => this.handleVisibilityChange();
    this.doc.addEventListener('visibilitychange', this.handler);

    if (this.doc.visibilityState === 'hidden') {
      this.backgroundEntryTime = this.now();
    }
  }

  isVisible(): boolean {
    return this.doc.visibilityState === 'visible';
  }

  getBackgroundDurationMs(): number {
    if (this.backgroundEntryTime === null) return 0;
    return this.now() - this.backgroundEntryTime;
  }

  onForeground(cb: (durationMs: number) => void): () => void {
    this.foregroundCallbacks.add(cb);
    return () => this.foregroundCallbacks.delete(cb);
  }

  onBackground(cb: () => void): () => void {
    this.backgroundCallbacks.add(cb);
    return () => this.backgroundCallbacks.delete(cb);
  }

  destroy(): void {
    this.doc.removeEventListener('visibilitychange', this.handler);
    this.backgroundCallbacks.clear();
    this.foregroundCallbacks.clear();
  }

  private handleVisibilityChange(): void {
    if (this.doc.visibilityState === 'hidden') {
      this.backgroundEntryTime = this.now();
      for (const cb of this.backgroundCallbacks) cb();
    } else {
      const duration =
        this.backgroundEntryTime !== null ? this.now() - this.backgroundEntryTime : 0;
      this.backgroundEntryTime = null;
      for (const cb of this.foregroundCallbacks) cb(duration);
    }
  }
}
